import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Pin, Check } from 'lucide-react';

interface Zone {
  id: string;
  name: string;
}

interface ZoneSelectorProps {
  value: string;
  onChange: (zoneId: string) => void;
  zones: Zone[];
  className?: string;
  pinnedZone?: string | null;
  onPinToggle?: (zoneId: string) => void;
}

const ZoneSelector: React.FC<ZoneSelectorProps> = ({
  value,
  onChange,
  zones,
  className = '',
  pinnedZone,
  onPinToggle
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allOptions = [{ id: 'all', name: 'All Zones' }, ...zones];
  const selectedOption = allOptions.find(z => z.id === value) || allOptions[0];

  const handleSelect = (zoneId: string) => {
    onChange(zoneId);
    setIsOpen(false);
  };

  const handlePinClick = (e: React.MouseEvent, zoneId: string) => {
    e.stopPropagation();
    onPinToggle?.(zoneId);
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Selected value button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="min-w-[220px] px-3 py-1.5 text-sm border border-slate-700/50 rounded-md bg-slate-800/50 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          {pinnedZone === value && (
            <Pin className="w-3.5 h-3.5 text-red-500 fill-current" />
          )}
          <span className="truncate">{selectedOption.name}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 min-w-[240px] bg-[#0C2340] border border-slate-700/50 rounded-md shadow-lg z-50 py-1 max-h-64 overflow-y-auto">
          {allOptions.map(zone => (
            <div
              key={zone.id}
              className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-700/50 ${
                value === zone.id ? 'bg-cyan-900/20' : ''
              }`}
              onClick={() => handleSelect(zone.id)}
            >
              <div className="flex items-center gap-2">
                {value === zone.id ? (
                  <Check className="w-4 h-4 text-cyan-400" />
                ) : (
                  <span className="w-4" />
                )}
                <span className="text-sm text-white">{zone.name}</span>
              </div>
              {onPinToggle && (
                <button
                  onClick={(e) => handlePinClick(e, zone.id)}
                  className={`p-1 rounded transition-colors ${
                    pinnedZone === zone.id
                      ? 'text-red-500'
                      : 'text-slate-600 hover:text-red-400'
                  }`}
                  title={pinnedZone === zone.id ? '取消釘選' : '釘選此區域'}
                >
                  <Pin className={`w-4 h-4 ${pinnedZone === zone.id ? 'fill-current' : ''}`} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ZoneSelector;

